"use client"
import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

type ModalKind = "info" | "success" | "error" | "warning" | "waiting";

interface ModalState {
  open: boolean;
  kind: ModalKind;
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  isConfirm?: boolean;
  resolver?: (value: boolean) => void;
}

interface ModalContextType {
  show: (kind: ModalKind, title: string, message?: string) => void;
  showInfo: (title: string, message?: string) => void;
  showSuccess: (title: string, message?: string) => void;
  showError: (title: string, message?: string) => void;
  showWarning: (title: string, message?: string) => void;
  showWaiting: (title: string, message?: string) => void;
  confirm: (title: string, message?: string, confirmText?: string, cancelText?: string) => Promise<boolean>;
  close: () => void;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export function ModalProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<ModalState>({ open: false, kind: "info" });

  const close = useCallback(() => {
    setState(prev => ({ ...prev, open: false, resolver: undefined, isConfirm: false }));
  }, []);

  const show = useCallback<ModalContextType["show"]>((kind, title, message) => {
    setState({ open: true, kind, title, message });
  }, []);

  const confirm = useCallback<ModalContextType["confirm"]>((title, message, confirmText = "Proceed", cancelText = "Cancel") => {
    return new Promise<boolean>((resolve) => {
      setState({ open: true, kind: "warning", title, message, isConfirm: true, confirmText, cancelText, resolver: resolve });
    });
  }, []);

  const ctx = useMemo<ModalContextType>(() => ({
    show,
    showInfo: (t, m) => show("info", t, m),
    showSuccess: (t, m) => show("success", t, m),
    showError: (t, m) => show("error", t, m),
    showWarning: (t, m) => show("warning", t, m),
    showWaiting: (t, m) => show("waiting", t, m),
    confirm,
    close,
  }), [show, confirm, close]);

  const accentByKind: Record<ModalKind, string> = {
    info: "#3b82f6",
    success: "#22c55e",
    error: "#ef4444",
    warning: "#f59e0b",
    waiting: "#6366f1",
  };

  return (
    <ModalContext.Provider value={ctx}>
      {children}

      {state.open && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60" onClick={state.isConfirm ? undefined : close} />
          <div className="relative z-10 w-full max-w-md mx-4 rounded-2xl border bg-[var(--primaryBackground)] text-gray-900 shadow-2xl" style={{ borderColor: "#11182720" }}>
            <div className="px-5 py-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-2.5 h-2.5 rounded-full" style={{ background: accentByKind[state.kind] }} />
                <h3 className="text-base font-semibold">{state.title}</h3>
              </div>
              {state.kind === "waiting" && (
                <div className="flex items-center gap-3 mb-2">
                  <div className="relative">
                    <div className="h-5 w-5 rounded-full border-2 border-gray-200/60 animate-spin border-t-transparent" />
                    <div className="absolute inset-0 h-5 w-5 rounded-full border-2" style={{ borderColor: accentByKind[state.kind], borderTopColor: "transparent" }} />
                  </div>
                  <span className="text-sm text-[var(--primaryText)]">Please wait…</span>
                </div>
              )}
              {state.message && <p className="text-sm text-[var(--primaryText)] whitespace-pre-line">{state.message}</p>}
              <div className="flex justify-end gap-2 mt-4">
                {state.isConfirm ? (
                  <>
                    <button
                      onClick={() => { state.resolver?.(false); close(); }}
                      className="px-3 py-1.5 rounded-md bg-gray-200 hover:bg-gray-300 text-[var(--primaryText)] transition"
                    >
                      {state.cancelText}
                    </button>
                    <button
                      onClick={() => { state.resolver?.(true); close(); }}
                      className="px-3 py-1.5 rounded-md text-[var(--primaryText)] transition"
                      style={{ background: accentByKind[state.kind] }}
                    >
                      {state.confirmText}
                    </button>
                  </>
                ) : (
                  <button
                    onClick={close}
                    className="px-3 py-1.5 rounded-md bg-gray-900 hover:bg-black text-[var(--primaryText)] transition"
                  >
                    Close
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </ModalContext.Provider>
  );
}

export const useModal = () => {
  const ctx = useContext(ModalContext);
  if (!ctx) throw new Error("useModal must be used within ModalProvider");
  return ctx;
};


